import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, pluck } from 'rxjs/operators';
import { isArray } from 'util';
import { environment } from '../../../../environments/environment';
import { ActiveModel } from '../../models/active-model';
import { LastMeasureProductionEventBuilder } from '../../models/builders/last-measurepproduction-event.builder';
import { ProgramBuilder } from '../../models/builders/program.builder';
import { SchedulerTaskProgramSyncBuilder } from '../../models/builders/scheduler-task-program-sync.builder';
import { SchedulerTaskBuilder } from '../../models/builders/scheduler-task.builder';
import { SchedulerTask } from '../../models/scheduler-task';
import { SchedulerTaskProgram } from '../../models/scheduler-task-program';
import { SchedulerTaskProgramChecklist } from '../../models/scheduler-task-program-checklist';
import { SchedulerTaskProgramConfiguration } from '../../models/scheduler-task-program-configuration';
import { mapCollection } from '../../utils/collection.utils';
import { parseNumber } from '../../utils/number.utils';
import { valueOf } from '../../utils/string.utils';
import { getAvoidErrorHandlingHeader } from '../base/headers';
// tslint:disable-next-line: max-line-length
import { ProgramConfiguration } from './../../../dashboard/scheduler/scheduler-configuration/scheduler-configuration-by-task/scheduler-configuration/scheduler-program-configure/program-configuration';

const createProgramsMeasureBody = (modelHash: string, programData: ProgramConfiguration[]) => {
  const programsData = (isArray(programData))
    ? programData.map(program => ({
      toolHash: program.schedulerTaskProgram.toolHash,
      toolHolderHash: program.schedulerTaskProgram.toolHolderHash,
      pocket: program.schedulerTaskProgram.pocket,
      programHash: program.schedulerTaskProgram.programHash,
    })
    ) : [];

  return { modelHash: modelHash, programs: programsData };
};

const createMeasuresToAvoidBody = (task: string, measuresToAvoid: any[]) => {
  const measuresToAvoidData = (isArray(measuresToAvoid))
    ? measuresToAvoid.map(measure => ({
      programReference: measure.programReference,
      productionEventHash: measure.productionEventHash,
      pocket: measure.pocket,
    })
    ) : [];

  return { task: task, measuresToAvoid: measuresToAvoidData };
};

const createSendZollerTaskProgramsBody = (
  task: string,
  issuePockets: {
    schedulerTaskProgramHash: string,
    pocket: number,
    toolHash: string,
    holderHash: string,
    scrapTool: boolean,
    scrapHolder: boolean,
    rebuildTool: boolean,
  }[]
) => {
  const taskPrograms = (isArray(issuePockets))
    ? issuePockets.map(measure => ({
      taskProgramHash: measure.schedulerTaskProgramHash,
      pocket: measure.pocket,
      toolHash: measure.toolHash,
      toolHolderHash: measure.holderHash,
      toScrapToolPublic: measure.scrapTool,
      toScrapToolHolder: measure.scrapHolder,
      toRebuildToolPublic: measure.rebuildTool,
    })) : [];

  return { task: task, taskPrograms: taskPrograms };
};

const createSendHaimerTaskProgramsBody = (
  task: string,
  issuePockets: {
    schedulerTaskProgramHash: string,
    pocket: number,
    toolHash: string,
    holderHash: string,
    scrapTool: boolean,
    scrapHolder: boolean,
    rebuildTool: boolean,
  }[]
) => {
  const taskPrograms = (isArray(issuePockets))
    ? issuePockets.map(measure => ({
      taskProgramHash: measure.schedulerTaskProgramHash,
      pocket: measure.pocket,
      toolHash: measure.toolHash,
      toolHolderHash: measure.holderHash,
      toScrapToolPublic: measure.scrapTool,
      toScrapToolHolder: measure.scrapHolder,
      toRebuildToolPublic: measure.rebuildTool,
    })) : [];

  return { task: task, taskPrograms: taskPrograms };
};

const createSchedulerTaskProgramsBody = (programs: SchedulerTaskProgram[]) => {
  return (isArray(programs))
    ? programs.map(program => ({
      program: program.programHash,
      print: program.printHash,
      projectNumber: program.manufacturingOrderProjectNumber
    })
    ) : [];
};

const createSchedulerTaskBody = (task: SchedulerTask) => {
  return {
    model: task.model,
    startDate: task.startDate,
    endDate: task.endDate,
    programs: createSchedulerTaskProgramsBody(task.programs),
    putToolTime: task.putToolTime,
    removeToolTime: task.removeToolTime,
    putPieceTime: task.putPieceTime,
    removePieceTime: task.removePieceTime,
    deviation: task.deviation
  };
};

const saveSchedulerTaskProgramsChecklistBody = (
  schedulerTask: string,
  checklists: SchedulerTaskProgramChecklist[],
  reservedPockets: number[]
) => {
  const checkLists = (isArray(checklists))
    ? checklists.map(checklist => ({
      hash: checklist.hash,
      schedulerTaskProgram: checklist.schedulerTaskProgramHash,
      pocket: checklist.pocket,
      checked: checklist.checked,
      ignorable: checklist.ignorableFixedTool || checklist.ignorableMasterTaskTool ? false : checklist.ignorable,
      imported: checklist.imported,
      ignorableFixedTool: checklist.ignorableFixedTool,
      twin: checklist.twin,
      generatedTwin: !!checklist.generatedTwin,
      ignorableMasterTask: checklist.ignorableMasterTaskTool,
      masterTaskHash: checklist.masterTaskHash,
      masterTaskToolPosition: checklist.masterTaskToolPosition,
      importedFromTaskHash: checklist.importedFromTaskHash
    }))
    : [];

  return { schedulerTask, checkLists, reservedPockets };
};

const splitSchedulerTaskBody = (
  schedulerTask: SchedulerTask,
  programs: SchedulerTaskProgram[]
) => {
  const programHashes = (isArray(programs))
    ? mapCollection(program => program.hash, programs)
    : [];

  return { schedulerTask: schedulerTask.hash, programs: programHashes };
};

const joinSchedulerTaskBody = (
  destinationTask: SchedulerTask,
  originTask: SchedulerTask
) => {

  return { destinationTask: destinationTask.hash, originTask: originTask.hash };
};

const saveSchedulerTaskProgramsConfigurationBody = (configurations: SchedulerTaskProgramConfiguration[]) => {
  return (isArray(configurations))
    ? configurations.map(configuration => ({
      schedulerTaskProgram: configuration.schedulerTaskProgramHash,
      origin: configuration.originHash,
      index: configuration.index,
      vibration: configuration.vibration,
      refrigeration: configuration.refrigeration,
      checkBreak: configuration.checkBreak,
      repetitions: configuration.repetitions,
      compensation: configuration.compensation,
      verticalCompensation: configuration.verticalCompensation
    }))
    : [];
};

const saveSchedulerTaskPrintConfigurationBody = (printConfigurations: any[]) => {
  return mapCollection(configuration => ({
    print: configuration.print,
    origin: configuration.origin,
    x: configuration.x,
    y: configuration.y,
    z: configuration.z,
    c: configuration.c
  }), printConfigurations);
};

@Injectable()
export class SchedulerTaskByTaskApiService {
  private apiUrl: string;

  constructor(private httpClient: HttpClient) {
    this.apiUrl = `${environment.apiUrl}scheduler-tasks`;
  }

  create(task: SchedulerTask) {
    return this.httpClient.post(`${this.apiUrl}`, createSchedulerTaskBody(task))
      .pipe(
        map(SchedulerTaskBuilder.fromJson)
      );
  }

  createSchedulerTaskProgramsConfiguration(
    task: string,
    programConfigurations: SchedulerTaskProgramConfiguration[],
    printConfigurations: any[],
  ) {
    const body = {
      task,
      programConfigurations: saveSchedulerTaskProgramsConfigurationBody(programConfigurations),
      printConfigurations: saveSchedulerTaskPrintConfigurationBody(printConfigurations),
    };

    return this.httpClient.post(`${this.apiUrl}/configuration`, body);
  }

  delete(task: SchedulerTask) {
    return this.httpClient.delete(`${this.apiUrl}/${task.hash}`);
  }

  finish(task: SchedulerTask) {
    const params = new HttpParams().append('hash', task.hash);

    return this.httpClient.put(`${this.apiUrl}/finish`, null, { params });
  }

  getReadySchedulerTasksByModelAndStatus(model: string) {
    const params = new HttpParams()
      .append('model', model);

    return this.httpClient.get(`${this.apiUrl}/ready`, { params })
      .pipe(map(SchedulerTaskBuilder.fromList));
  }

  getProgramTimesByModel(model: string, programs: string[]) {
    const params = new HttpParams()
      .append('modelHash', model)
      .append('programsHashes', programs.toString()
      );
    return this.httpClient.get(`${this.apiUrl}/programTimes`, { params })
      .pipe(map(ProgramBuilder.fromList));
  }

  getSchedulerTaskFreeSlot(model: string, date: number, hash: string) {
    const params = new HttpParams()
      .append('model', model)
      .append('date', valueOf(date))
      .append('hash', hash);

    return this.httpClient.get(`${this.apiUrl}/free-slot`, { params, responseType: 'text' })
      .pipe(map(parseNumber));
  }

  getSchedulerTaskPrograms(task: string) {
    const params = new HttpParams().append('task', task);

    return this.httpClient.get(`${this.apiUrl}/programs`, { params })
      .pipe(
        map(SchedulerTaskBuilder.buildProgramsFromList)
      );
  }

  getSchedulerTaskProgramsConfiguration(task: string) {
    const params = new HttpParams().append('task', task);

    return this.httpClient.get(`${this.apiUrl}/configuration`, { params })
      .pipe(
        map(SchedulerTaskBuilder.buildConfigurationFromList)
      );
  }

  getSchedulerTaskPrintsConfiguration(task: string) {
    const params = new HttpParams().append('task', task);

    return this.httpClient.get(`${this.apiUrl}/print-configuration`, { params })
      .pipe(
        map(SchedulerTaskBuilder.buildPrintConfigurationFromList)
      );
  }

  getSchedulerTasks(start: number, end: number) {
    const params = new HttpParams()
      .append('start', valueOf(start))
      .append('end', valueOf(end));

    return this.httpClient.get(`${this.apiUrl}`, { params })
      .pipe(map(SchedulerTaskBuilder.fromList));
  }

  getSchedulerTaskProgramsChecklist(task: string) {
    const params = new HttpParams().append('task', task);

    return this.httpClient.get(`${this.apiUrl}/checklist`, { params })
      .pipe(map(SchedulerTaskBuilder.buildChecklistsFromJson));
  }

  getSchedulerTasksStatus(start: number, end: number) {
    const params = new HttpParams()
      .append('start', valueOf(start))
      .append('end', valueOf(end));
    const headers = getAvoidErrorHandlingHeader();

    return this.getFilteredSchedulerTasksStatus(params, headers);
  }

  getSchedulerTasksStatusByModel(start: number, end: number, model: ActiveModel) {
    const params = new HttpParams()
      .append('start', valueOf(start))
      .append('end', valueOf(end))
      .append('model', model.hash);

    return this.getFilteredSchedulerTasksStatus(params);
  }

  getAllSchedulerTasksStatusByModel(start: number, end: number, model: ActiveModel) {
    const params = new HttpParams()
      .append('start', valueOf(start))
      .append('end', valueOf(end))
      .append('model', model.hash);

    return this.httpClient.get(`${this.apiUrl}/all-status`, { params })
      .pipe(map(SchedulerTaskBuilder.fromList));
  }

  getSuggestions(
    taskHash: string,
    schedulerTaskProgramHash: string,
    parent: boolean
  ) {
    const params = new HttpParams()
      .append('task', taskHash)
      .append('program', schedulerTaskProgramHash)
      .append('parent', parent.toString());

    return this.httpClient.get(`${this.apiUrl}/suggestions`, { params })
      .pipe(map(SchedulerTaskBuilder.buildChecklistsFromList));
  }

  importZollerFromTask(from: string, to: string, parent: boolean) {
    const params = new HttpParams()
      .append('from', from)
      .append('to', to)
      .append('parent', parent.toString());

    return this.httpClient.post<{ importedCount: number }>(`${this.apiUrl}/zoller/checklist/import`, {}, { params })
      .pipe(
        pluck('importedCount'),
        map(parseNumber)
      );
  }

  importHaimerFromTask(from: string, to: string, parent: boolean) {
    const params = new HttpParams()
      .append('from', from)
      .append('to', to)
      .append('parent', parent.toString());

    return this.httpClient.post<{ importedCount: number }>(`${this.apiUrl}/haimer/checklist/import`, {}, { params })
      .pipe(
        pluck('importedCount'),
        map(parseNumber)
      );
  }

  searchSchedulerTasks(identifier: string) {
    const params = new HttpParams().append('identifier', identifier);

    return this.httpClient.get(`${this.apiUrl}/search`, { params })
      .pipe(
        map(SchedulerTaskBuilder.fromList)
      );
  }

  sendZoller(task: SchedulerTask, onlyNew: boolean = false) {
    const params = new HttpParams()
      .append('task', task.hash)
      .append('newOnly', onlyNew.toString());

    return this.httpClient.post(`${this.apiUrl}/zoller`, null, { params });
  }

  sendHaimer(task: SchedulerTask, onlyNew: boolean = false) {
    const params = new HttpParams()
      .append('task', task.hash)
      .append('newOnly', onlyNew.toString());

    return this.httpClient.post(`${this.apiUrl}/haimer`, null, { params });
  }

  sendProduction(task: string, measurementsToAvoid: any[]) {
    return this.httpClient.post(`${this.apiUrl}/production`, createMeasuresToAvoidBody(task, measurementsToAvoid));
  }

  sendZollerTaskPrograms(task: string, issuePockets: {
    schedulerTaskProgramHash: string,
    pocket: number,
    toolHash: string,
    holderHash: string,
    scrapTool: boolean,
    scrapHolder: boolean,
    rebuildTool: boolean,
  }[]) {
    return this.httpClient.post(`${this.apiUrl}/zoller-tools`, createSendZollerTaskProgramsBody(task, issuePockets));
  }

  sendHaimerTaskPrograms(task: string, issuePockets: {
    schedulerTaskProgramHash: string,
    pocket: number,
    toolHash: string,
    holderHash: string,
    scrapTool: boolean,
    scrapHolder: boolean,
    rebuildTool: boolean,
  }[]) {
    return this.httpClient.post(`${this.apiUrl}/haimer-tools`, createSendHaimerTaskProgramsBody(task, issuePockets));
  }

  sync(model: ActiveModel) {
    const params = new HttpParams().append('modelHash', model.hash);

    return this.httpClient.post(`${this.apiUrl}/sync`, null, { params })
      .pipe(
        map(SchedulerTaskProgramSyncBuilder.fromJson)
      );
  }

  update(task: SchedulerTask) {
    return this.httpClient.put(`${this.apiUrl}/${task.hash}`, createSchedulerTaskBody(task))
      .pipe(
        map(SchedulerTaskBuilder.fromJson)
      );
  }

  split(task: SchedulerTask, programs: SchedulerTaskProgram[]) {
    return this.httpClient.post(`${this.apiUrl}/split`, splitSchedulerTaskBody(task, programs));
  }

  join(destination: SchedulerTask, origin: SchedulerTask) {
    return this.httpClient.post(`${this.apiUrl}/join`, joinSchedulerTaskBody(destination, origin));
  }

  updateZollerSchedulerTaskProgramsChecklist(
    schedulerTask: string,
    checklists: SchedulerTaskProgramChecklist[],
    reservedPockets: number[]
  ) {
    return this.httpClient.put(`${this.apiUrl}/zoller/checklist`,
      saveSchedulerTaskProgramsChecklistBody(schedulerTask, checklists, reservedPockets)
    );
  }

  updateHaimerSchedulerTaskProgramsChecklist(
    schedulerTask: string,
    checklists: SchedulerTaskProgramChecklist[],
    reservedPockets: number[]
  ) {
    return this.httpClient.put(`${this.apiUrl}/haimer/checklist`,
      saveSchedulerTaskProgramsChecklistBody(schedulerTask, checklists, reservedPockets)
    );
  }

  validateZollerSchedulerTask(task: string) {
    const params = new HttpParams().append('task', task);

    return this.httpClient.put(`${this.apiUrl}/zoller/validate`, null, { params });
  }

  validateHaimerSchedulerTask(task: string) {
    const params = new HttpParams().append('task', task);

    return this.httpClient.put(`${this.apiUrl}/haimer/validate`, null, { params });
  }

  private getFilteredSchedulerTasksStatus(params: HttpParams, headers?: HttpHeaders) {
    return this.httpClient.get(`${this.apiUrl}/status`, { params, headers })
      .pipe(map(SchedulerTaskBuilder.fromList));
  }

  getLastMeasurementsEvent(taskHash: string, modelHash: string, programData: ProgramConfiguration[]) {
    return this.httpClient.post(`${this.apiUrl}/${taskHash}/measurement-by-programs`, createProgramsMeasureBody(modelHash, programData))
      .pipe(map(LastMeasureProductionEventBuilder.fromList));
  }
}



// WEBPACK FOOTER //
// /var/www/borneo/prod/borneo_app/src/app/core/services/api/scheduler-task-by-task-api.service.ts